import { useState } from 'react';
import { useHotspotDefault, useMe, useUnitHotspot } from '@glyph/core';
import { Button, Card, CardHeader, Dialog, Field, Note, StatusPill, TextInput, useToast } from '@glyph/ui';

/** Who may see / change hotspot passwords in the cloud (the API enforces the same). */
const DEFAULT_ROLES = new Set(['owner', 'admin', 'platform']);
const UNIT_ROLES = new Set(['owner', 'admin', 'platform', 'event_admin']);
const MIN = 8;
const MAX = 63;
const validPw = (pw: string) => pw.length >= MIN && pw.length <= MAX && /^[\x20-\x7e]+$/.test(pw);

const STATUS: Record<string, { label: string; tone: 'ok' | 'warn' | 'bad' | 'fg4' }> = {
  applied: { label: 'ON THE UNIT', tone: 'ok' },
  waiting: { label: 'WAITING FOR THE UNIT', tone: 'warn' },
  sending: { label: 'SENDING', tone: 'fg4' },
  failed: { label: 'RETRYING', tone: 'warn' },
  none: { label: 'NOT SET BY THE CLOUD', tone: 'fg4' },
};

function useCopy() {
  const toast = useToast();
  return async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.push('Copied', 'ok');
    } catch {
      toast.push('Select the text and copy it', 'warn');
    }
  };
}

/** Password text with Show / Copy. Hidden until asked for: the console may be on a shared screen. */
function Reveal({ value, label }: { value: string; label: string }) {
  const [show, setShow] = useState(false);
  const copy = useCopy();
  return (
    <div className="flex min-w-0 items-center gap-1.5">
      <span className="min-w-0 flex-1 truncate font-mono text-md" aria-label={label}>
        {show ? value : '•'.repeat(Math.min(12, value.length))}
      </span>
      <Button size="xs" variant="ghost" mono onClick={() => setShow(!show)}>
        {show ? 'HIDE' : 'SHOW'}
      </Button>
      <Button size="xs" variant="ghost" mono onClick={() => void copy(value)}>
        COPY
      </Button>
    </div>
  );
}

/** Devices page: the password every unit's hotspot uses unless a unit has its own. New boxes get it on their first
 * connection; offline units when they come back. */
export function HotspotDefaultCard({ className }: { className?: string }) {
  const me = useMe();
  const allowed = DEFAULT_ROLES.has(me.data?.role ?? '');
  const d = useHotspotDefault(allowed);
  const toast = useToast();
  const [editing, setEditing] = useState<string | null>(null);
  if (!allowed) return null;
  const pw = d.data?.password ?? null;
  const save = async () => {
    if (editing == null || !validPw(editing)) return;
    try {
      const r = await d.set(editing);
      toast.push(`Hotspot password sent to ${r.units_following} unit${r.units_following === 1 ? '' : 's'}${r.units_waiting ? ` · ${r.units_waiting} get it when they connect` : ''}`, 'ok');
      setEditing(null);
    } catch (e) {
      toast.push((e as Error).message || 'Could not set the password', 'bad');
    }
  };
  return (
    <Card className={className}>
      <CardHeader title="Hotspot password" meta={pw ? 'EVERY UNIT' : 'NOT SET'} metaTone={pw ? 'ok' : 'warn'} />
      <div className="flex flex-col gap-3 border-t border-line px-4 py-3">
        {pw ? <Reveal value={pw} label="Hotspot password" /> : <Note tone="warn">No default yet: each unit still has the random password it made on first boot.</Note>}
        {d.data && pw && (
          <span className="font-mono text-xs text-fg4">
            {d.data.units_following} FOLLOW IT{d.data.units_custom ? ` · ${d.data.units_custom} HAVE THEIR OWN` : ''}{d.data.units_waiting ? ` · ${d.data.units_waiting} WAITING TO CONNECT` : ''}
          </span>
        )}
        <Button size="responsive" onClick={() => setEditing(pw ?? '')}>
          {pw ? 'Change' : 'Set a default'}
        </Button>
        <Note>New units get it the first time they connect. A unit's own password is on its Network page.</Note>
      </div>
      <Dialog
        open={editing != null}
        onClose={() => setEditing(null)}
        title="Hotspot password for every unit"
        meta="ANYONE ON A UNIT'S HOTSPOT REJOINS WITH THE NEW ONE"
        confirm={{ label: 'Save and send', onClick: save, disabled: editing == null || !validPw(editing), busy: d.busy }}
      >
        <div className="flex flex-col gap-2">
          <Field label={`Password · ${MIN} to ${MAX} characters`}>
            <TextInput variant="mono" block autoFocus autoComplete="off" value={editing ?? ''} onChange={(e) => setEditing(e.target.value)} aria-label="New hotspot password" />
          </Field>
          <Note>Units online now switch straight away; the others when they connect. Units with their own password keep it.</Note>
        </div>
      </Dialog>
    </Card>
  );
}

/** Network page (cloud): this unit's hotspot password as the cloud set it, and whether the unit has it yet. */
export function UnitHotspotPassword({ deviceId }: { deviceId: string }) {
  const me = useMe();
  const allowed = UNIT_ROLES.has(me.data?.role ?? '');
  const h = useUnitHotspot(deviceId, allowed);
  const toast = useToast();
  const [editing, setEditing] = useState<string | null>(null);
  if (!allowed) return <Note>The hotspot password is managed by your admins.</Note>;
  const data = h.data;
  const st = STATUS[data?.status ?? 'none'] ?? STATUS.none!;
  const save = async (pw: string | null) => {
    try {
      const r = await h.set(pw);
      toast.push(pw == null ? 'Back on the default password' : r.status === 'applied' ? 'Hotspot password changed' : 'Saved · the unit gets it when it connects', 'ok');
      setEditing(null);
    } catch (e) {
      toast.push((e as Error).message || 'Could not set the password', 'bad');
    }
  };
  return (
    <div className="flex min-w-0 flex-col gap-2" data-testid="unit-hotspot">
      {data?.password ? <Reveal value={data.password} label="This unit's hotspot password" /> : <span className="font-mono text-sm text-fg4">Random, set by the unit on first boot</span>}
      <div className="flex flex-wrap items-center gap-2">
        <StatusPill tone={st.tone}>{st.label}</StatusPill>
        <span className="font-mono text-xs text-fg4">{data?.source === 'custom' ? 'OWN PASSWORD' : data?.source === 'default' ? 'ORGANISATION DEFAULT' : ''}</span>
      </div>
      {data?.detail && <Note>{data.detail}</Note>}
      <div className="flex flex-wrap gap-2">
        <Button size="xs" onClick={() => setEditing(data?.source === 'custom' && data.password ? data.password : '')}>
          Own password…
        </Button>
        {data?.source === 'custom' && (
          <Button size="xs" variant="ghost" onClick={() => void save(null)} disabled={h.busy}>
            Use the default
          </Button>
        )}
      </div>
      <Dialog
        open={editing != null}
        onClose={() => setEditing(null)}
        title="Hotspot password for this unit"
        meta="ANYONE ON ITS HOTSPOT REJOINS WITH THE NEW ONE"
        confirm={{ label: 'Save and send', onClick: () => save(editing ?? ''), disabled: editing == null || !validPw(editing), busy: h.busy }}
      >
        <Field label={`Password · ${MIN} to ${MAX} characters`}>
          <TextInput variant="mono" block autoFocus autoComplete="off" value={editing ?? ''} onChange={(e) => setEditing(e.target.value)} aria-label="This unit's new hotspot password" />
        </Field>
      </Dialog>
    </div>
  );
}
